import React, { useState, useEffect } from 'react';
import { addToFirebase, checkEmailExists } from './services/firebaseService';
import { sendInscriptionConfirmation } from './services/emailVerificationService';
import EmailVerificationModal from './components/EmailVerificationModal';
import SuccessModal from './components/SuccessModal';

function LandingPage() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  // Constantes de validation
  const MAX_NAME_LENGTH = 200;
  const MAX_EMAIL_LENGTH = 255;
  const MAX_PHONE_LENGTH = 20;
  const PHONE_MIN_LENGTH = 10; // Format français minimum

  // Fonction pour valider le format du téléphone français
  const isValidPhoneNumber = (phone) => {
    // Supprimer les espaces pour la validation
    const numbers = phone.replace(/\s/g, '');
    // Format français : 10 chiffres, peut commencer par 0 ou +33
    const phoneRegex = /^(?:(?:\+|00)33|0)[1-9](?:[\s.-]*\d{2}){4}$/;
    return phoneRegex.test(numbers) && numbers.length >= PHONE_MIN_LENGTH && numbers.length <= MAX_PHONE_LENGTH;
  };

  // Fonction pour formater le numéro de téléphone avec des espaces tous les 2 chiffres
  const formatPhoneNumber = (value) => {
    // Supprimer tous les espaces et caractères non numériques
    const numbers = value.replace(/\D/g, '');
    
    // Limiter à 10 chiffres (format français)
    const limitedNumbers = numbers.slice(0, 10);
    
    // Ajouter un espace tous les 2 chiffres
    const formatted = limitedNumbers.match(/.{1,2}/g)?.join(' ') || limitedNumbers;
    
    return formatted;
  };

  // Ouvrir la modal de vérification
  const handleOpenVerificationModal = () => {
    const trimmedEmail = email.trim();
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedPhone = phone.replace(/\s/g, '').trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!trimmedEmail || !trimmedFirstName || !trimmedLastName || !trimmedPhone) {
      setError('Veuillez remplir tous les champs');
      return;
    }
    
    if (!emailRegex.test(trimmedEmail)) {
      setError('Veuillez entrer un email valide');
      return;
    }

    if (!isValidPhoneNumber(trimmedPhone)) {
      setError('Veuillez entrer un numéro de téléphone valide (format français)');
      return;
    }

    if (emailExists) {
      setError('Cet email est déjà inscrit. Veuillez utiliser un autre email.');
      return;
    }

    if (isCheckingEmail) {
      setError('Vérification de l\'email en cours, veuillez patienter...');
      return;
    }
    
    setIsVerificationModalOpen(true);
  };

  // Fonction pour soumettre le formulaire (extrait de handleSubmit)
  const submitForm = async () => {
    setError('');
    
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedEmail = email.trim();
    // Supprimer les espaces du numéro de téléphone avant sauvegarde
    const trimmedPhone = phone.replace(/\s/g, '').trim();

    // Vérifier que tous les champs sont remplis
    if (!trimmedFirstName || !trimmedLastName || !trimmedEmail || !trimmedPhone) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    // Validation des longueurs
    if (trimmedFirstName.length > MAX_NAME_LENGTH) {
      setError(`Le prénom ne peut pas dépasser ${MAX_NAME_LENGTH} caractères`);
      return;
    }

    if (trimmedLastName.length > MAX_NAME_LENGTH) {
      setError(`Le nom ne peut pas dépasser ${MAX_NAME_LENGTH} caractères`);
      return;
    }

    if (trimmedEmail.length > MAX_EMAIL_LENGTH) {
      setError(`L'email ne peut pas dépasser ${MAX_EMAIL_LENGTH} caractères`);
      return;
    }

    if (trimmedPhone.length > MAX_PHONE_LENGTH) {
      setError(`Le téléphone ne peut pas dépasser ${MAX_PHONE_LENGTH} caractères`);
      return;
    }

    // Validation format email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setError('Veuillez entrer un email valide');
      return;
    }

    // Validation format téléphone
    if (!isValidPhoneNumber(trimmedPhone)) {
      setError('Veuillez entrer un numéro de téléphone valide (format français)');
      return;
    }

    // Vérifier si l'email existe déjà (dernière vérification avant soumission)
    if (emailExists) {
      setError('Cet email est déjà inscrit. Veuillez utiliser un autre email.');
      return;
    }

    // Attendre que la vérification soit terminée si elle est en cours
    if (isCheckingEmail) {
      setError('Vérification de l\'email en cours, veuillez patienter...');
      return;
    }

    setIsSubmitting(true);

    try {
      // Sauvegarder l'inscription dans Firebase
      const fullName = `${trimmedFirstName} ${trimmedLastName}`.trim();
      const firebaseSuccess = await addToFirebase(fullName, trimmedEmail, trimmedPhone);
      
      if (!firebaseSuccess) {
        throw new Error('Échec de la sauvegarde dans Firebase. Vérifiez votre configuration Firebase.');
      }

      // Envoyer l'email de confirmation (ne pas bloquer si échec)
      try {
        await sendInscriptionConfirmation(trimmedEmail, trimmedFirstName);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Email de confirmation non envoyé:', err);
        }
      }

      // Arrêter le loading avant d'ouvrir la modal
      setIsSubmitting(false);
      
      // Ouvrir la modal de succès
      setIsSuccessModalOpen(true);
    } catch (err) {
      // Ne pas exposer les détails de l'erreur à l'utilisateur
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ [LandingPage] Erreur lors de l\'inscription:', err);
      }
      
      // Messages d'erreur génériques pour ne pas exposer d'informations sensibles
      let errorMessage = 'Une erreur est survenue lors de la sauvegarde. Veuillez réessayer.';
      
      if (err.message && err.message.includes('dépasser')) {
        // Conserver les messages de validation de longueur
        errorMessage = err.message;
      } else if (err.message && err.message.includes('Format')) {
        // Conserver les messages de validation de format
        errorMessage = err.message;
      } else if (err.code === 'permission-denied') {
        errorMessage = 'Erreur de permissions. Veuillez contacter le support.';
      } else if (err.code === 'unavailable') {
        errorMessage = 'Service temporairement indisponible. Veuillez réessayer plus tard.';
      }
      
      setError(errorMessage);
      setIsSubmitting(false);
    }
  };

  // Callback quand l'email est vérifié dans la modal
  const handleEmailVerified = async () => {
    setEmailVerified(true);
    setIsVerificationModalOpen(false);
    
    // Soumettre automatiquement le formulaire après la vérification
    await submitForm();
  };

  // Fonction pour vérifier si l'email existe déjà (avec debounce)
  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    // Réinitialiser l'état si l'email est vide
    if (!email || !email.trim()) {
      setEmailExists(false);
      setIsCheckingEmail(false);
      return;
    }

    // Vérifier que l'email est valide avant de faire la requête
    if (!emailRegex.test(email.trim())) {
      setEmailExists(false);
      setIsCheckingEmail(false);
      return;
    }

    // Debounce : attendre 800ms après que l'utilisateur ait arrêté de taper
    const timeoutId = setTimeout(async () => {
      setIsCheckingEmail(true);
      try {
        const exists = await checkEmailExists(email);
        setEmailExists(exists);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Erreur lors de la vérification de l\'email:', error);
        }
        setEmailExists(false);
      } finally {
        setIsCheckingEmail(false);
      }
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [email]);

  // Fonction pour réinitialiser le formulaire
  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setEmailVerified(false);
    setError('');
    setIsSubmitting(false);
    setEmailExists(false);
    setIsCheckingEmail(false);
  };

  // Callback quand on ferme la modal de succès
  const handleCloseSuccessModal = () => {
    setIsSuccessModalOpen(false);
    resetForm();
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Vérifier que l'email est vérifié
    if (!emailVerified) {
      setError('Veuillez vérifier votre email avant de continuer');
      return;
    }

    // Utiliser la fonction submitForm
    await submitForm();
  };

  return (
    <>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        html, body {
          margin: 0;
          padding: 0;
          width: 100%;
          min-height: 100%;
        }

        @media (min-width: 969px) {
          html, body {
            height: 100%;
            overflow: hidden;
          }
        }

        #root {
          margin: 0;
          padding: 0;
          width: 100%;
          min-height: 100vh;
        }

        @media (min-width: 969px) {
          #root {
            height: 100vh;
            overflow: hidden;
          }
        }

        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
            'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
            sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          background: linear-gradient(135deg, #0a0a0a 0%, #000000 100%);
          color: #ffffff;
        }

        .landing-page {
          width: 100%;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          padding: 20px;
        }

        @media (max-width: 968px) {
          .landing-page {
            align-items: flex-start;
            padding: 24px 20px calc(48px + env(safe-area-inset-bottom));
            min-height: 100vh;
          }
        }

        .landing-page::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: 
            radial-gradient(circle at 20% 30%, rgba(214, 171, 120, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(214, 171, 120, 0.08) 0%, transparent 50%);
          pointer-events: none;
          z-index: 0;
        }

        .landing-container {
          max-width: 1200px;
          width: 100%;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: center;
          position: relative;
          z-index: 1;
        }

        .landing-content {
          display: flex;
          flex-direction: column;
          gap: 30px;
        }

        .logo {
          margin-bottom: 0px;
        }

        .logo img {
          height: 50px;
          width: auto;
        }

        .landing-title {
          font-size: 56px;
          font-weight: 700;
          line-height: 1.2;
          margin: 0;
          font-family: 'Outfit', sans-serif;
          color: #ffffff;
        }

        .landing-title-accent {
          display: block;
          color: #d6ab78;
        }

        .landing-subtitle {
          font-size: 22px;
          line-height: 1.6;
          opacity: 0.9;
          margin: 0;
          font-family: 'Inter', sans-serif;
          color: rgba(255, 255, 255, 0.9);
        }

        .landing-features {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .landing-features li {
          font-size: 18px;
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.95);
          font-family: 'Inter', sans-serif;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .landing-features li::before {
          content: '✓';
          color: #d6ab78;
          font-size: 22px;
          font-weight: 700;
          flex-shrink: 0;
        }

        .landing-form-container {
          background: rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 24px;
          padding: 50px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .form-title {
          font-size: 32px;
          font-weight: 700;
          margin: 0 0 12px 0;
          font-family: 'Outfit', sans-serif;
          color: #ffffff;
        }

        .form-subtitle {
          font-size: 16px;
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.7);
          margin: 0 0 30px 0;
          font-family: 'Inter', sans-serif;
        }

        .form-group {
          margin-bottom: 24px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 24px;
        }

        .form-row .form-group {
          margin-bottom: 0;
        }

        @media (max-width: 640px) {
          .form-row {
            grid-template-columns: 1fr;
            gap: 24px;
          }

          .form-row .form-group {
            margin-bottom: 24px;
          }
        }

        .form-label {
          display: block;
          font-size: 14px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.9);
          margin-bottom: 8px;
          font-family: 'Inter', sans-serif;
        }

        .form-input {
          width: 100%;
          padding: 16px 20px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          font-size: 16px;
          color: #ffffff;
          font-family: 'Inter', sans-serif;
          transition: all 0.3s ease;
        }

        .form-input::placeholder {
          color: rgba(255, 255, 255, 0.5);
        }

        .form-input:focus {
          outline: none;
          border-color: #d6ab78;
          background: rgba(255, 255, 255, 0.15);
          box-shadow: 0 0 0 3px rgba(214, 171, 120, 0.2);
        }

        .form-input-error {
          border-color: #ff6b6b !important;
          background: rgba(255, 107, 107, 0.1) !important;
        }

        .form-input-error:focus {
          border-color: #ff6b6b !important;
          box-shadow: 0 0 0 3px rgba(255, 107, 107, 0.2) !important;
        }

        .form-button {
          width: 100%;
          padding: 18px 32px;
          background: #d6ab78;
          color: #ffffff;
          border: none;
          border-radius: 12px;
          font-size: 18px;
          font-weight: 600;
          font-family: 'Inter', sans-serif;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 10px;
        }

        .form-button:hover:not(:disabled) {
          background: #c49a67;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(214, 171, 120, 0.4);
        }

        .form-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .form-error {
          background: rgba(255, 76, 76, 0.2);
          border: 1px solid rgba(255, 76, 76, 0.5);
          color: #ff6b6b;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 14px;
          margin-top: 16px;
          font-family: 'Inter', sans-serif;
        }

        .landing-visual {
          position: absolute;
          right: 5%;
          top: 50%;
          transform: translateY(-50%);
          z-index: 0;
          opacity: 0.3;
        }

        .landing-visual-content {
          width: 400px;
          height: 400px;
          background: rgba(255, 255, 255, 0.03);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid rgba(255, 255, 255, 0.1);
          position: relative;
          overflow: hidden;
        }

        .landing-visual-content::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(214, 171, 120, 0.1) 0%, transparent 70%);
          animation: rotate 20s linear infinite;
        }

        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .landing-visual-icon {
          font-size: 120px;
          color: rgba(214, 171, 120, 0.2);
          position: relative;
          z-index: 1;
        }

        @media (max-width: 968px) {
          .landing-container {
            grid-template-columns: 1fr;
            gap: 50px;
            text-align: center;
          }

          .landing-title {
            font-size: 42px;
          }

          .landing-subtitle {
            font-size: 18px;
          }

          .landing-features li {
            font-size: 16px;
          }

          .landing-form-container {
            padding: 40px 30px;
          }

          .form-title {
            font-size: 28px;
          }

          .landing-visual {
            display: none;
          }
        }

        @media (max-width: 640px) {
          .landing-page {
            padding: 15px;
          }

          .landing-title {
            font-size: 36px;
          }

          .landing-subtitle {
            font-size: 16px;
          }

          .landing-form-container {
            padding: 30px 20px;
          }

          .form-title {
            font-size: 24px;
          }
        }
      `}</style>
      <div className="landing-page">
        <div className="landing-container">
          <div className="landing-content">
            <div className="logo">
              <img src="/logo copy.png" alt="HEYA" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
            <h1 className="landing-title">
              Rejoignez en
              <span className="landing-title-accent"> avant-première</span>
            </h1>
            <p className="landing-subtitle">
              La solution d'analyse de solvabilité qui révolutionne l'immobilier. 
              Soyez parmi les premiers à découvrir notre plateforme innovante.
            </p>
            <ul className="landing-features">
              <li>Accès prioritaire à la plateforme</li>
              <li>Analyse de solvabilité en moins d'une minute</li>
              <li>Certificats numériques certifiés</li>
              <li>Sécurité de niveau bancaire</li>
            </ul>
          </div>

          <div className="landing-form-container">
            <h2 className="form-title">Réservez votre accès</h2>
            <p className="form-subtitle">
              Inscrivez-vous dès maintenant pour être parmi les premiers à utiliser HEYA.
            </p>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="email" className="form-label">Email</label>
                <input
                  type="email"
                  id="email"
                  className={`form-input ${emailExists ? 'form-input-error' : ''}`}
                  placeholder="votre@email.com"
                  value={email}
                  maxLength={MAX_EMAIL_LENGTH}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, MAX_EMAIL_LENGTH);
                    setEmail(value);
                    setEmailExists(false);
                    if (emailVerified) setEmailVerified(false);
                  }}
                  required
                />
                {isCheckingEmail && email.trim() && (
                  <div className="form-email-checking" style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.6)', marginTop: '8px', fontFamily: 'Inter, sans-serif' }}>
                    Vérification de l'email...
                  </div>
                )}
                {emailExists && !isCheckingEmail && (
                  <div className="form-email-error" style={{ fontSize: '14px', color: '#ff6b6b', marginTop: '8px', fontFamily: 'Inter, sans-serif' }}>
                    Cet email est déjà inscrit. Veuillez utiliser un autre email.
                  </div>
                )}
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName" className="form-label">Prénom</label>
                  <input
                    type="text"
                    id="firstName"
                    className="form-input"
                    placeholder="Votre prénom"
                    value={firstName}
                    maxLength={MAX_NAME_LENGTH}
                    onChange={(e) => setFirstName(e.target.value.slice(0, MAX_NAME_LENGTH))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName" className="form-label">Nom</label>
                  <input
                    type="text"
                    id="lastName"
                    className="form-input"
                    placeholder="Votre nom"
                    value={lastName}
                    maxLength={MAX_NAME_LENGTH}
                    onChange={(e) => setLastName(e.target.value.slice(0, MAX_NAME_LENGTH))}
                    required
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="phone" className="form-label">Téléphone</label>
                <input
                  type="tel"
                  id="phone"
                  className="form-input"
                  placeholder="06 12 34 56 78"
                  value={phone}
                  maxLength={MAX_PHONE_LENGTH + 4}
                  onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
                  required
                />
              </div>
              
              {error && <div className="form-error">{error}</div>}
              
              {!emailVerified ? (
                <button
                  type="button"
                  className="form-button"
                  onClick={handleOpenVerificationModal}
                  style={{ marginTop: '10px' }}
                >
                  Réserver votre accès
                </button>
              ) : (
                <button
                  type="submit"
                  className="form-button"
                  disabled={isSubmitting}
                  style={{ marginTop: '10px' }}
                >
                  {isSubmitting ? 'Inscription en cours...' : 'S\'inscrire en avant-première'}
                </button>
              )}
            </form>
          </div>
        </div>

        <div className="landing-visual">
          <div className="landing-visual-content">
            {/* Fusée supprimée */}
          </div>
        </div>
      </div>
      
      <EmailVerificationModal
        email={email}
        isOpen={isVerificationModalOpen}
        onClose={() => setIsVerificationModalOpen(false)}
        onVerified={handleEmailVerified}
      />
      
      <SuccessModal
        isOpen={isSuccessModalOpen}
        onClose={handleCloseSuccessModal}
      />
    </>
  );
}

export default LandingPage;
